/**
 * Product Offerings Tabbed Component
 * Handles tab switching with animations and carousel pagination
 */

class ProductOfferingsTabbed extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.product-offerings-tabbed__category-link');
    this.panels = this.querySelectorAll('.product-offerings-tabbed__panel');
    this.currentTab = 0;
    this.init();
  }

  init() {
    this.initTabs();
    this.initCarousels();
    this.initPopups();
  }

  initTabs() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.target.classList.contains('product-offerings-tabbed__category-link') || e.target.closest('.product-offerings-tabbed__category-link')) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.switchTab((this.currentTab + 1) % this.tabs.length);
          this.tabs[this.currentTab].focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.switchTab((this.currentTab - 1 + this.tabs.length) % this.tabs.length);
          this.tabs[this.currentTab].focus();
        }
      }
    });
  }

  switchTab(index) {
    if (index === this.currentTab) return;

    // Update tabs
    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    // Update panels with animation
    this.panels.forEach((panel, i) => {
      const isActive = i === index;

      if (isActive) {
        panel.hidden = false;
        // Force reflow for animation
        panel.offsetHeight;
        panel.classList.add('is-active');

        // Re-trigger item and image animations
        const items = panel.querySelectorAll('.product-offerings-tabbed__item');
        const images = panel.querySelectorAll('.product-offerings-tabbed__img');

        items.forEach((item) => {
          item.style.animation = 'none';
          item.offsetHeight;
          item.style.animation = '';
        });

        images.forEach((img) => {
          img.style.animation = 'none';
          img.offsetHeight;
          img.style.animation = '';
        });
      } else {
        panel.classList.remove('is-active');
        // Delay hiding for exit animation
        setTimeout(() => {
          if (!panel.classList.contains('is-active')) {
            panel.hidden = true;
          }
        }, 400);
      }
    });

    this.currentTab = index;
  }

  openPopup(popupId) {
    const popup = this.querySelector(
      '.product-offerings-tabbed__popup-overlay[data-popup="' + popupId + '"]'
    );
    if (popup) {
      popup.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }
  }

  initPopups() {
    // Use event delegation so it works with dynamically rendered content and scroll duplicates
    this.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-popup-trigger]');
      if (!trigger) return;

      e.preventDefault();
      this.openPopup(trigger.getAttribute('data-popup-trigger'));
    });

    // Keyboard: Enter/Space on popup triggers
    this.addEventListener('keydown', (e) => {
      const trigger = e.target.closest('[data-popup-trigger]');
      if (!trigger) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openPopup(trigger.getAttribute('data-popup-trigger'));
      }
    });

    // Close popup on overlay click or close button
    this.addEventListener('click', (e) => {
      const overlay = e.target.closest('.product-offerings-tabbed__popup-overlay');
      if (!overlay) return;

      // Clicked the overlay background itself or the close button
      if (e.target === overlay || e.target.closest('.product-offerings-tabbed__popup-close')) {
        overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const active = this.querySelector('.product-offerings-tabbed__popup-overlay.is-active');
        if (active) {
          active.classList.remove('is-active');
          document.body.style.overflow = '';
        }
      }
    });
  }

  initCarousels() {
    this.panels.forEach((panel) => {
      const wrapper = panel.querySelector('.product-offerings-tabbed__grid-wrapper');
      if (!wrapper || wrapper.dataset.hasCarousel !== 'true') return;

      const pages = panel.querySelectorAll('.product-offerings-tabbed__grid-page');
      const prevBtn = panel.querySelector('.product-offerings-tabbed__nav-btn--prev');
      const nextBtn = panel.querySelector('.product-offerings-tabbed__nav-btn--next');
      const dots = panel.querySelectorAll('.product-offerings-tabbed__dot');

      if (pages.length <= 1) return;

      let currentPage = 1;
      const totalPages = pages.length;

      const goToPage = (pageNum) => {
        if (pageNum < 1 || pageNum > totalPages) return;

        currentPage = pageNum;

        pages.forEach((page) => {
          const isActive = parseInt(page.dataset.page) === currentPage;
          page.classList.toggle('is-active', isActive);
        });

        dots.forEach((dot) => {
          const isActive = parseInt(dot.dataset.page) === currentPage;
          dot.classList.toggle('is-active', isActive);
        });

        if (prevBtn) prevBtn.disabled = currentPage === 1;
        if (nextBtn) nextBtn.disabled = currentPage === totalPages;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => goToPage(currentPage - 1));
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => goToPage(currentPage + 1));
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const pageNum = parseInt(dot.dataset.page);
          goToPage(pageNum);
        });
      });

      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      wrapper.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      wrapper.addEventListener(
        'touchend',
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;

          if (Math.abs(diff) > 50) {
            if (diff > 0) {
              goToPage(currentPage + 1);
            } else {
              goToPage(currentPage - 1);
            }
          }
        },
        { passive: true }
      );
    });
  }
}

customElements.define('product-offerings-tabbed', ProductOfferingsTabbed);
